import type * as Actions from './actions';
import {
  loadMediaEventsFailed,
  loadMediaEventsRequest,
  loadMediaEventsSucceeded,
  loadRouteInfoFailed,
  loadSavedRoutesFailed,
  loadSavedRoutesRequest,
  loadSavedRoutesSucceeded,
  loadTrackingFailed,
  loadTrackingSucceeded,
} from './actionCreators';
import { ActionTypes } from './actionTypes';
import moment from 'moment';
import { all, call, put, select, takeLatest } from 'redux-saga/effects';

import {
  type DateRange,
  type EventFilters,
  EventTrigger,
  type Media,
  MediaType,
  type PageableContent,
  type Session,
} from '@cobliteam/common-types';

import {
  type FullTrackingGraphQL,
  GrahpQLTrackingRequester,
  GraphQLCameraMediaRequester,
  GraphQLRoutingRequester,
  type NewRoute,
  type WebData,
} from '@cobliteam/api-lib';

import { AlertTypes, showAlertActionCreator } from '@cobliteam/alert';
import { RoutingPermissions } from '@cobliteam/routing/src/permissions';

import { selectLoginWebData } from '@cobliteam/store';

import { checkAccessAllowed } from '@cobliteam/hooks/src/usePermissions';

const trackingRequester = new GrahpQLTrackingRequester();
const routingGraphQLRequester = new GraphQLRoutingRequester();
const mediaGraphQLRequester = new GraphQLCameraMediaRequester();

export function* requestRouteInfoSaga(action: Actions.LoadRouteInfoRequest) {
  const { deviceId, vehicleId, date, mediaEventsEnabled } = action.payload;

  const dateMoment = moment(date);
  const start = dateMoment.startOf('day').valueOf();
  const end = dateMoment.endOf('day').valueOf();

  try {
    if (mediaEventsEnabled) {
      yield put(loadMediaEventsRequest(start, end, deviceId));
    }

    yield put(loadSavedRoutesRequest(date, vehicleId));
  } catch (err) {
    yield put(loadRouteInfoFailed());
  }
}

export function* requestTrackingGraphQLSaga(
  action: Actions.LoadTrackingRequest
) {
  const { vehicleId, startTimestamp, endTimestamp, useMapMatch } =
    action.payload;

  try {
    const trackingGraphQL: FullTrackingGraphQL = yield call(
      trackingRequester.getFullTrackingInfo,
      vehicleId,
      startTimestamp,
      endTimestamp,
      useMapMatch
    );

    yield put(loadTrackingSucceeded(trackingGraphQL));
  } catch (err) {
    yield put(loadTrackingFailed());
  }
}

export function* loadSavedRoutesRequestSaga(
  action: Actions.LoadSavedRoutesRequest
) {
  const { date, vehicleId } = action.payload;
  const period: DateRange = {
    start: moment(date).startOf('day').valueOf(),
    end: moment(date).endOf('day').valueOf(),
  };

  try {
    const loginWebData: WebData<Session> = yield select(selectLoginWebData);

    const newRoutes: NewRoute[] = yield call(
      routingGraphQLRequester.getTrackingRoutes,
      period,
      vehicleId,
      checkAccessAllowed(
        loginWebData,
        RoutingPermissions.VIEW_PROOF_OF_CONCLUSION
      )
    );

    yield put(loadSavedRoutesSucceeded(newRoutes));
  } catch (err) {
    yield put(loadSavedRoutesFailed());
  }
}

export function* requestMediaEventsSaga(
  action: Actions.LoadMediaEventsRequest
) {
  const { deviceId, startInMillis, endInMillis } = action.payload;

  try {
    const period: DateRange = {
      start: startInMillis,
      end: endInMillis,
    };

    const eventCount: number = yield call(
      mediaGraphQLRequester.getMediaEventCount,
      deviceId,
      period
    );

    const mediaFilters: EventFilters = {
      size: eventCount,
      mediaPeriod: period,
      vehicleIds: [deviceId],
      mediaTypes: [MediaType.EVENT_VIDEO],
      eventTriggers: [EventTrigger.TAILGATING, EventTrigger.DISTRACTED_DRIVING],
    };

    const events: PageableContent<Media> = yield call(
      mediaGraphQLRequester.getMediaEventList,
      mediaFilters,
      0
    );

    yield put(loadMediaEventsSucceeded(events.content));
  } catch (err) {
    yield put(loadMediaEventsFailed());
    yield put(
      showAlertActionCreator(
        AlertTypes.ERROR,
        'Houve um erro, tente novamente mais tarde.'
      )
    );
  }
}

export function* TrackingSagasListener() {
  yield all([
    takeLatest(ActionTypes.LOAD_TRACKING_REQUEST, requestTrackingGraphQLSaga),
    takeLatest(ActionTypes.LOAD_ROUTE_INFO_REQUEST, requestRouteInfoSaga),
    takeLatest(
      ActionTypes.LOAD_SAVED_ROUTES_REQUEST,
      loadSavedRoutesRequestSaga
    ),
    takeLatest(ActionTypes.LOAD_MEDIA_EVENTS_REQUEST, requestMediaEventsSaga),
  ]);
}
